import React, { useState, useEffect, useRef, useCallback } from "react";
import { UserData } from "@/entities/users/types";
import { useMyRoleType } from "@/entities/users/hooks";
import { ChatMessage } from "@/entities/messaging/types";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import Text from "@/components/ui/text";
import NewChatBottomBar from "@/components/global/Chat/components/chat/NewChatBottomBar";
import { HeroHeaderQuestions } from "@/components/layouts/HeroHeader/HeroHeaderQuestions";
import backgroundVideo from "@/assets/animations/DreamerAnimation.webm";
import { cn } from "@/utils/utils";
import HeroHeaderMessage from "@/components/layouts/HeroHeader/HeroHeaderMessage";
import { useTranslation } from "react-i18next";
import { getTimeOfDay } from "@/utils/datetime";
import { UserRoles } from "@/services/features/types";
import { useNotificationManager } from "@/hooks/useNotificationManager";
import { DreamerState } from "@/components/controls/DreamerButton/DreamerButton";

interface Props {
  userData: UserData | undefined;
  setMessageFromHeader: React.Dispatch<React.SetStateAction<string>>;
  sendQuestionDirectly: (questionText: string) => void;
  externalState: DreamerState;
  setChatDrawerOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface SuggestedMessage {
  message: string;
  timestamp: number;
}

const HeroHeader: React.FC<Props> = ({
  userData,
  setMessageFromHeader,
  sendQuestionDirectly,
  externalState,
  setChatDrawerOpen
}: Props) => {
  const { t } = useTranslation();
  const { waitingForMessage } = useChat();
  const { roleType } = useMyRoleType();

  const isLeader = [
    UserRoles.ACCOUNT_EXECUTIVE_LEADER,
    UserRoles.SALES_DEVELOPMENT_REPRESENTATIVE_LEADER,
    UserRoles.HEAD_OF_SALES_ORGANIZATION,
    UserRoles.SALES_ADMIN,
    UserRoles.CUSTOMER_SUCCESS_TECHNICAL_MANAGER,
    UserRoles.DREAMHUB_ADMIN,
    UserRoles.MARKETING_LEADER,
    UserRoles.HEAD_OF_MARKETING_ORGANIZATION,
    UserRoles.MARKETING_ADMIN,
    UserRoles.CUSTOMER_SUCCESS_MANAGER_LEADER
  ].includes(roleType ?? -1);

  const videoRef = useRef<HTMLVideoElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [hadPreviousNotifications, setHadPreviousNotifications] =
    useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(1);

  const timeOfDay = getTimeOfDay(new Date());

  // Check if browser supports CSS scroll-driven animations
  const supportsScrollTimeline =
    typeof CSS !== "undefined" &&
    CSS.supports("animation-timeline", "scroll()");

  // JavaScript fallback for scroll-based fade effect (for Safari and other browsers without support)
  const handleScroll = useCallback(() => {
    if (!headerRef.current || supportsScrollTimeline) return;

    const header = headerRef.current;
    const headerRect = header.getBoundingClientRect();
    const headerHeight = header.offsetHeight;

    // Calculate how much of the header has scrolled out of view
    // When header top is at viewport top (0), we start fading
    // When 80% of header has scrolled out, opacity should be 0
    const scrolledOut = Math.max(0, -headerRect.top);
    const fadeThreshold = headerHeight * 0.8;
    const opacity = Math.max(0, 1 - scrolledOut / fadeThreshold);

    setScrollOpacity(opacity);
  }, [supportsScrollTimeline]);

  // Set up scroll listener for JS fallback
  useEffect(() => {
    if (supportsScrollTimeline) return; // CSS handles it

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll, supportsScrollTimeline]);

  const { notificationCount } = useNotificationManager({
    notifications: [],
    externalState,
    channelName: "notifications.dreamerbutton.dreamhub"
  });

  useEffect(() => {
    if (notificationCount > 0) {
      setHadPreviousNotifications(true);
    }
  }, [notificationCount]);

  const handleVideoCanPlay = () => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 1.2;
      // Safari requires explicit play() call for autoplay
      videoRef.current.play().catch(() => {});
    }
    setVideoLoaded(true);
  };

  return (
    <header
      ref={headerRef}
      className="hero-header relative bg-surface-elevation0 text-secondary p-0 w-full min-w-[768px] overflow-hidden"
      style={!supportsScrollTimeline ? { opacity: scrollOpacity } : undefined}
    >
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          src={backgroundVideo}
          autoPlay
          loop
          muted
          playsInline
          onCanPlay={handleVideoCanPlay}
          className={cn(
            "w-full h-full object-cover transition-opacity duration-1000 ease-in-out",
            videoLoaded ? "opacity-100" : "opacity-0"
          )}
        />
      </div>
      <div className="relative z-10 flex w-full flex-col items-center justify-center px-6 pt-16 pb-20 h-[480px]">
        <div className="flex w-full flex-col items-center justify-center max-w-[1700px] p-6 gap-14">
          <div className="flex flex-col gap-2 justify-center items-center">
            <Text variant="title3H1">
              {t(timeOfDay === "night" ? "nightGreeting" : "greeting", {
                timeOfDay,
                firstName: userData?.firstName || ""
              })}
            </Text>
            <HeroHeaderMessage
              isLeader={isLeader}
              notificationCount={notificationCount}
              setChatDrawerOpen={setChatDrawerOpen}
              hadPreviousNotifications={hadPreviousNotifications}
            />
          </div>
          <div className="flex flex-col justify-center items-center p-6 gap-4 xxl:p-8 xxl:gap-5 md:w-[960px] sm:w-full lg:w-[1000px] xl:w-[1200px] xxl:w-[1480px] rounded-[48px] xxl:rounded-[56px] border border-white-8 backdrop-blur-[16px] shadow-dreamerHeader bg-white-4">
            <NewChatBottomBar
              selectedUser={userData!}
              sendMessage={(newMessage: ChatMessage) =>
                setMessageFromHeader(newMessage.message)
              }
              lastMessageFromSelectedUser={waitingForMessage}
              isFromHeader
            />
            <HeroHeaderQuestions sendQuestionDirectly={sendQuestionDirectly} />
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 w-full h-[100px] xxl:h-[150px] bg-gradient-to-t from-[#121926] to-[#121926]/0"></div>
    </header>
  );
};

export default HeroHeader;
